import { useState, type ReactNode } from 'react';
import { useStore } from '../state/store';

/**
 * One titled block of the side panel, with its title as the button that folds
 * it away.
 *
 * The heading text is unchanged from the plain `<h3>` this replaces — the
 * button lives INSIDE the `<h3>`, so "Placement" is still a level-3 heading
 * with that exact text, and every locator that reads the panel by its headings
 * still finds them. What is new is `aria-expanded` and the body it controls.
 *
 * Open by default and collapsed per `id` in `viewer.collapsed`, which is
 * persisted: a shop that never touches the Realism sliders should not have to
 * scroll past them every session. The ids are hand-written and stable for that
 * reason — deriving them from the title would silently re-open every section
 * the day a word in a heading changes.
 *
 * `defaultCollapsed` is what a section reads before anyone has folded it — true
 * for the inspector, whose drawer starts shut behind its summary line. The
 * toggle flips the EFFECTIVE state (`toggleSection(id, !collapsed)`), not the
 * stored one, so the first click on a section that starts shut opens it instead
 * of writing the state it already has.
 *
 * `summary` is the one thing allowed beside the title, and it LEADS it: the
 * inspector's "All checks pass" line, which is the whole point of folding that
 * section away, so it reads first and the fold button ("Details") sits at the
 * row's end. It stays visible while the section is folded, because a verdict
 * that disappears when you fold the list is a verdict you have to unfold to
 * read. (It replaces the trailing `aside` slot, whose one user — the
 * inspector's "3 warn" count — this line is.)
 *
 * `remember={false}` makes the fold per-MOUNT scratch instead: the sections inside the
 * template editor are part of one dialog, not a panel the shop lives in, and folding
 * "Template" once should not open the next "New template…" with the Name field hidden
 * (`autoFocus` cannot land there, the trap focuses "Choose photo…", and "Save template"
 * stays disabled with the reason "name" inside a section nobody can see is folded).
 */
export function Section({ id, title, summary, defaultCollapsed = false, remember = true, children }: {
  id: string; title: string; summary?: ReactNode; defaultCollapsed?: boolean; remember?: boolean; children: ReactNode;
}) {
  const stored = useStore((s) => s.viewer.collapsed[id]);
  const toggleSection = useStore((s) => s.toggleSection);
  const [scratch, setScratch] = useState(defaultCollapsed);
  const collapsed = remember ? (stored ?? defaultCollapsed) : scratch;
  return (
    <SectionView
      id={id} title={title} summary={summary} collapsed={collapsed}
      onToggle={() => (remember ? toggleSection(id, !collapsed) : setScratch(!collapsed))}
    >
      {children}
    </SectionView>
  );
}

/**
 * The markup, with the folded/unfolded state handed to it. Split from `Section`
 * so it can be rendered and asserted on its own: zustand answers a server
 * render from the store's INITIAL state, so a component that reads the store
 * itself cannot be rendered to a string in any state but the default one.
 */
export function SectionView({ id, title, summary, collapsed, onToggle, children }: {
  id: string; title: string; summary?: ReactNode; collapsed: boolean; onToggle: () => void; children: ReactNode;
}) {
  const bodyId = `section-${id}`;
  return (
    <section className="section" data-id={id}>
      <div className="row section-head">
        {summary}
        <h3>
          <button
            type="button" className="section-toggle"
            aria-expanded={!collapsed} aria-controls={bodyId}
            onClick={onToggle}
          >
            {title}
          </button>
        </h3>
      </div>
      {/* Rendered even when empty so `aria-controls` always names a real
          element; the children themselves come and go, so a folded section
          costs nothing to lay out and holds no focusable control. */}
      <div id={bodyId} className="section-body">{!collapsed && children}</div>
    </section>
  );
}
